import React from 'react';
import { Trophy, Medal } from 'lucide-react';

// Labels are now loaded from the API. This is a fallback for display.
const formatMetricLabel = (key) => {
  return key.replace(/^lb_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getRankStyle = (rank) => {
  if (rank === 1) return 'bg-blue-50 border-l-4 border-blue-500';
  if (rank === 2) return 'bg-gray-50 border-l-4 border-gray-400';
  if (rank === 3) return 'bg-orange-50 border-l-4 border-orange-400';
  return '';
};

const getRankIcon = (rank) => {
  if (rank === 1) return <Trophy size={16} className="text-blue-500" />;
  if (rank === 2) return <Medal size={16} className="text-gray-400" />;
  if (rank === 3) return <Medal size={16} className="text-orange-400" />;
  return <span className="text-sm text-gray-400 font-rasa w-4 text-center">{rank}</span>;
};

const LeaderboardTable = ({ rankings, metrics }) => {
  if (!rankings || rankings.length === 0) {
    return (
      <div className="bg-gray-50 p-8 rounded-lg text-center">
        <Trophy size={32} className="mx-auto mb-2 text-gray-300" />
        <p className="text-gray-500 font-rasa">No rankings yet. Add members to get started.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-w-full">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider font-rasa w-12">#</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider font-rasa">User</th>
            {metrics.map(key => (
              <th key={key} className="text-right py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider font-rasa whitespace-nowrap">
                {formatMetricLabel(key)}
              </th>
            ))}
            <th className="text-right py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider font-rasa">Total</th>
          </tr>
        </thead>
        <tbody>
          {rankings.map((entry, index) => {
            const rank = index + 1;
            return (
              <tr key={entry.user_public_id} className={`${getRankStyle(rank)} transition-colors hover:bg-blue-50/50`}>
                <td className="py-3 px-3">
                  <div className="flex items-center justify-center">
                    {getRankIcon(rank)}
                  </div>
                </td>
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    {entry.avatar_url ? (
                      <img src={entry.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold flex-shrink-0">
                        {(entry.full_name || entry.username || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-700 font-rasa">{entry.full_name || entry.username}</p>
                      <p className="text-xs text-gray-400 font-rasa">@{entry.username}</p>
                    </div>
                  </div>
                </td>
                {metrics.map(key => (
                  <td key={key} className="py-3 px-3 text-right">
                    <span className="text-sm text-gray-700 font-rasa font-medium">
                      {(entry.metrics && entry.metrics[key]) || 0}
                    </span>
                  </td>
                ))}
                <td className="py-3 px-3 text-right">
                  <span className="text-sm font-bold text-blue-600 font-rasa">{entry.total || 0}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LeaderboardTable;
